import { Component, DestroyRef, Input, OnInit, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts/highmaps';
import 'highcharts/modules/drilldown';
import { CamelCasePipe } from '../../../utils/camel-case.pipe';
import { MapDrilldownConfig, MapDrilldownTableColumn } from '../../../model/chart.model';
import { DownloadButtonComponent } from '../../download-button/download-button.component';
import { ExcelExportService } from '../../../services/excel-export.service';
import { CommonUtils } from '../../../utils/common.utils';
import { API_STATUS, SearchConfig } from '../../../model/api.model';
import { CommonService } from '../../../services/common.service';

const REGION_COLORS: Record<string, string> = {
    'Western Area':  '#005645',
    'Southern Area': '#E8540A',
    'Central Area':  '#1D4E6B',
    'Eastern Area':  '#9B59B6',
};

const REGION_STATES: Record<string, string[]> = {
    'Western Area':  ['us-ak','us-az','us-ca','us-co','us-hi','us-id','us-mt','us-nm','us-nv','us-or','us-ut','us-wa','us-wy'],
    'Southern Area': ['us-al','us-ar','us-fl','us-ga','us-ky','us-la','us-ms','us-nc','us-ok','us-sc','us-tn','us-tx'],
    'Central Area':  ['us-ia','us-il','us-in','us-ks','us-mi','us-mn','us-mo','us-nd','us-ne','us-oh','us-sd','us-wi'],
    'Eastern Area':  ['us-ct','us-dc','us-de','us-ma','us-md','us-me','us-nh','us-nj','us-ny','us-pa','us-ri','us-va','us-vt','us-wv'],
};
const WESTERN_AREA = 'Western Area';
const SOUTHERN_AREA = 'Southern Area';
const CENTRAL_AREA = 'Central Area';
const EASTERN_AREA = 'Eastern Area';


const REGION_NAME_ALIASES: Record<string, string> = {
  'WESTERN AREA': WESTERN_AREA,
  'WEST': WESTERN_AREA,
  'SOUTHERN AREA': SOUTHERN_AREA,
  'SOUTH': SOUTHERN_AREA,
  'SOUTHEAST': SOUTHERN_AREA,
  'CENTRAL AREA': CENTRAL_AREA,
  'CENTRAL': CENTRAL_AREA,
  'MIDWEST': CENTRAL_AREA,
  'EASTERN AREA': EASTERN_AREA,
  'EAST': EASTERN_AREA,
  'NORTHEAST': EASTERN_AREA,
};

const canonicalizeRegionName = (raw: string): string => {
    const trimmed = `${raw ?? ''}`.trim();
    if (!trimmed) {
        return '';
    }
    const alias = REGION_NAME_ALIASES[trimmed.toUpperCase()];
    return alias ?? trimmed;
};


@Component({
    selector: 'app-ds-map-drilldown',
    standalone: true,
    imports: [CommonModule, HighchartsChartModule, CamelCasePipe, DownloadButtonComponent],
    templateUrl: './ds-map-drilldown.component.html',
    styleUrl: './ds-map-drilldown.component.scss',
})
export class DsMapDrilldownComponent implements OnInit {
    @Input() chartSchema: any;
    @Input() dataInput: Array<any> = [];
    @Input() elementId = '';
    @Input() exportConfig: any;
    @Input() apiConfig?: SearchConfig;
    @Input() dataTransform?: (data: any[]) => any[];
    @Input() globalPayloadSubject?: Observable<any>;
    @Input() globalFilterFields: string[] = [];
    @Input() infoFields: any[] = [];
    @Input() onLoadFetch = true;

    Highcharts: typeof Highcharts = Highcharts;
    chartConstructor = 'mapChart';
    chartOptions: Highcharts.Options = {};
    updateFlag = false;
    isLoading = true;
    selectedState: string | null = null;
    selectedStateKey: string | null = null;
    selectedStateRegion: string | null = null;
    selectedStateDealers: Array<any> = [];
    private stateToRegionMap: Record<string, string> = {};
    private selectedArea = '';
    private mapSeriesData: Record<string, any[]> = {};
    private mapViewConfig: any;
    private chart?: any;

    private readonly ngZone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);
    private readonly commonUtils = inject(CommonUtils);
    private readonly exportService = inject(ExcelExportService);
    private readonly commonService = inject(CommonService);

    ngOnInit(): void {
        this.globalPayloadSubject?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((payload) => {
            const area = payload?.Value?.body?.area;
            this.selectedArea = canonicalizeRegionName(Array.isArray(area) ? area[0] : area ?? '');
            this.clearSelection();
            if (this.apiConfig?.urlPath && this.apiConfig?.path) {
                void this.fetchMapDataAndInitWithAreaFilter();
            } else {
                this.applyAreaHighlight();
            }
        });

        if (this.apiConfig?.urlPath && this.apiConfig?.path) {
            void this.fetchMapDataAndInit();
            return;
        }
        this.initMap();
    }

    clearSelection(): void {
        this.selectedState = null;
        this.selectedStateKey = null;
        this.selectedStateRegion = null;
        this.selectedStateDealers = [];
    }

    get chartTitle(): string {
        const title = this.chartSchema?.titleSchema?.title ?? this.chartSchema?.title ?? 'Dealer Distribution by US State';
        if (!this.isAreaFiltered) {
            return title;
        }
        return /\barea$/i.test(title.trim()) ? title.trim().replace(/area$/i, this.selectedArea) : `${title} - ${this.selectedArea}`;
    }

    private get isAreaFiltered(): boolean {
        return !!this.selectedArea && !!this.mapSeriesData[this.selectedArea];
    }

    get chartSubtitle(): string {
        return this.chartSchema?.titleSchema?.subTitle ?? this.chartSchema?.subtitle ?? '';
    }

    get selectedStateTotalAssociates(): number {
        return this.selectedStateDealers.reduce((sum, dealer) => sum + dealer.totalAssociates, 0);
    }

    get selectedStateTotalSales(): string {
        const total = this.selectedStateDealers.reduce((s, d) => s + d.salesRevenue, 0);
        return total > 0 ? `$${(total / 1_000_000).toFixed(1)}M` : '$0';
    }

    get mapConfig(): MapDrilldownConfig {
        return this.chartSchema?.mapDrilldownConfig ?? {
            tableColumns: [
                { label: 'Dealer Code', fieldKey: 'dlrCode', className: 'dealer-code' },
                { label: 'Dealer Name', fieldKey: 'name', className: 'dealer-name' },
                { label: 'Area', fieldKey: 'region', valueType: 'regionBadge' },
                { label: 'Total Associates', fieldKey: 'totalAssociates', valueType: 'number', className: 'num associates' },
            ],
            areaValueFieldKey: 'totalAssociates',
            stateValueFieldKey: 'totalAssociates',
            sortFieldKey: 'totalAssociates',
            areaMetricLabel: 'Total Associates By Area',
            stateMetricLabel: 'State Associates',
            footerMetricLabel: 'Total Associates',
            footerMetricFieldKey: 'totalAssociates',
            valueType: 'number',
        };
    }

    get tableColumns(): Array<MapDrilldownTableColumn> {
        return this.mapConfig.tableColumns;
    }

    get activeExportConfig(): any {
        return this.exportConfig ?? this.chartSchema?.exportConfig;
    }

    get exportRows(): Array<any> {
        return this.dataInput ?? [];
    }

    get calculatedInfoFields(): any[] {
        if (!this.infoFields || this.infoFields.length === 0) {
            return [];
        }

        return this.infoFields.map(field => {
            let value: any = 0;
            const calculateBy = field.calculateBy;
            if (!calculateBy) {
                return { ...field, value: '' };
            }
            if (this.dataInput && this.dataInput.length > 0) {
                if (calculateBy === 'totalAssociates' || calculateBy === 'total_associate_count') {
                    value = this.dataInput.reduce((sum, row) => sum + (row.totalAssociates || row.total_associate_count || 0), 0);
                } else if (calculateBy === 'totalSales' || calculateBy === 'total_sales' || calculateBy === 'salesRevenue') {
                    value = this.dataInput.reduce((sum, row) => sum + (row.totalSales || row.total_sales || row.salesRevenue || 0), 0);
                } else if (calculateBy === 'count') {
                    value = this.dataInput.length;
                } else {
                    value = this.dataInput.reduce((sum, row) => sum + (row[calculateBy] || 0), 0);
                }
            }
            let displayValue = '';
            if (calculateBy.includes('sales') || calculateBy.includes('revenue') || calculateBy.includes('Sales')) {
                displayValue = value > 0 ? `$${(value / 1_000_000).toFixed(1)}M` : '$0';
            } else {
                displayValue = value.toString();
            }

            return { ...field, value: displayValue };
        });
    }

    downloadActionOnClick(): void {
        const exportConfig = this.activeExportConfig;
        if (!exportConfig) {
            return;
        }
        if (exportConfig?.groupedSheets?.useResponseKeys && exportConfig.groupedSheets.schemaMap) {
            this.exportNestedSheets(exportConfig);
            return;
        }
        const data = this.commonUtils.formatExcelData(exportConfig, this.exportRows);
        this.exportService.exportAsExcelFile(data, exportConfig.fileName ?? '', exportConfig.descriptions ?? []);
    }

    private exportNestedSheets(exportConfig: any): void {
        const schemaMap = exportConfig.groupedSheets?.schemaMap ?? {};
        const rows = this.exportRows ?? [];
        const dataset: Record<string, any[]> = {};
        if (exportConfig.schema && rows.length > 0) {
            const mainSheetName = exportConfig.groupedSheets?.mainSheetName ?? 'Summary';
            const mainSheetData = this.commonUtils.formatExcelData({ ...exportConfig, schema: exportConfig.schema }, rows);
            dataset[mainSheetName] = structuredClone(mainSheetData) ?? [];
        }
        Object.entries(schemaMap).forEach(([sourceKey, sheetConfig]: [string, any]) => {
            if (sourceKey === 'dealers') {
                const allDealers: any[] = [];
                rows.forEach(row => {
                    const dealerRows = row?.stateBreakdown ?? row?.state_breakdown ?? [];
                    if (Array.isArray(dealerRows)) {
                        dealerRows.forEach(dealerRow => {
                            allDealers.push({
                                dlrCode: dealerRow?.dealerCode ?? dealerRow?.dealer_code ?? '',
                                name: dealerRow?.dealerName ?? dealerRow?.dealer_name ?? '',
                                region: row?.area ?? row?.region ?? row?.name ?? '',
                                state: dealerRow?.state ?? dealerRow?.stateCode ?? '',
                                totalAssociates: dealerRow?.associateCount ?? dealerRow?.associate_count ?? 0,
                                certifiable: dealerRow?.certifiable ?? dealerRow?.certifiable_count ?? 0,
                                nonCertifiable: dealerRow?.nonCertifiable ?? dealerRow?.non_certifiable_count ?? 0,
                                certified: dealerRow?.certified ?? dealerRow?.certified_count ?? 0,
                                master: dealerRow?.master ?? 0,
                                senior: dealerRow?.senior ?? 0,
                                foundation: dealerRow?.foundation ?? 0,
                            });
                        });
                    }
                });
                if (allDealers.length > 0) {
                    const sheetName = sheetConfig?.sheetName ?? sourceKey;
                    const schema = sheetConfig?.schema ?? sheetConfig;
                    dataset[sheetName] = structuredClone(this.commonUtils.formatExcelData({ ...exportConfig, schema }, allDealers)) ?? [];
                }
            } else {
                const sheetRows = rows.flatMap(row => Array.isArray(row?.[sourceKey]) ? row[sourceKey] : []);
                if (!sheetRows.length) { return; }
                const sheetName = sheetConfig?.sheetName ?? sourceKey;
                const schema = sheetConfig?.schema ?? sheetConfig;
                dataset[sheetName] = structuredClone(this.commonUtils.formatExcelData({ ...exportConfig, schema }, sheetRows)) ?? [];
            }
        });

        if (!Object.keys(dataset).length) {
            this.commonUtils.showMatSnackBar('Export Failed - No Data Found');
            return;
        }
        this.exportService.exportToMultipleSheets(dataset, exportConfig.fileName ?? '');
    }

    get selectedStateFooterMetric(): string {
        const total = this.selectedStateDealers.reduce((sum, dealer) => sum + Number(dealer?.[this.mapConfig.footerMetricFieldKey] ?? 0), 0);
        return this.formatValue(total, this.mapConfig.valueType ?? 'number');
    }

    getCellValue(dealer: any, column: MapDrilldownTableColumn): any {
        if (column.fieldKey === 'region') {
            return this.selectedStateRegion ?? dealer.region;
        }
        return dealer?.[column.fieldKey];
    }

    formatCellValue(value: any, column: MapDrilldownTableColumn): string {
        return this.formatValue(value, column.valueType ?? 'text');
    }

    private formatValue(value: any, valueType: string): string {
        const numericValue = Number(value ?? 0);
        if (valueType === 'currencyMillions') {
            return numericValue > 0 ? `$${(numericValue / 1_000_000).toFixed(1)}M` : '$0';
        }
        if (valueType === 'number') {
            return `${numericValue}`;
        }
        return value ?? '';
    }

    private buildStateDealerData(): Record<string, { dealers: Array<any>; totalAssociates: number; salesTotal: number; salesRevenue: number; dealerCount: number }> {
        return this.buildStateDataFromAreaRows();
    }

    private buildStateDataFromAreaRows(): Record<string, { dealers: Array<any>; totalAssociates: number; salesTotal: number; salesRevenue: number; dealerCount: number }> {
        const result: Record<string, { dealers: Array<any>; totalAssociates: number; salesTotal: number; salesRevenue: number; dealerCount: number }> = {};
        this.dataInput.forEach((areaRow) => {
            const area = canonicalizeRegionName(areaRow?.area ?? areaRow?.region ?? areaRow?.name ?? '');
            const dealerRows = areaRow?.stateBreakdown ?? areaRow?.state_breakdown ?? [];
            if (!area || !Array.isArray(dealerRows)) {
                return;
            }
            dealerRows.forEach((dealerRow: any) => {
                const state = `${dealerRow?.state ?? dealerRow?.stateCode ?? ''}`.trim().toLowerCase();
                if (!state) {
                    return;
                }
                const key = state.startsWith('us-') ? state : `us-${state}`;
                const dealerCode = `${dealerRow?.dealerCode ?? dealerRow?.dealer_code ?? ''}`;
                const associateCount = Number(dealerRow?.associateCount ?? dealerRow?.associate_count ?? 0);
                const row = {
                    id: dealerCode || key,
                    dlrCode: dealerCode,
                    name: dealerRow?.dealerName ?? dealerRow?.dealer_name ?? dealerCode,
                    region: area,
                    state: state.toUpperCase(),
                    totalAssociates: associateCount,
                    certifiable: Number(dealerRow?.certifiable ?? dealerRow?.certifiable_count ?? 0),
                    nonCertifiable: Number(dealerRow?.nonCertifiable ?? dealerRow?.non_certifiable_count ?? 0),
                    certified: Number(dealerRow?.certified ?? dealerRow?.certified_count ?? 0),
                    nonCertified: Number(dealerRow?.nonCertified ?? dealerRow?.non_certified_count ?? 0),
                    master: Number(dealerRow?.master ?? 0),
                    senior: Number(dealerRow?.senior ?? 0),
                    foundation: Number(dealerRow?.foundation ?? 0),
                };
                result[key] ??= { dealers: [], totalAssociates: 0, salesTotal: 0, salesRevenue: 0, dealerCount: 0 };
                result[key].dealers.push(row);
                result[key].totalAssociates += associateCount;
                result[key].dealerCount += 1;
            });
        });
        return result;
    }

    private buildRegionMetricMap(): Record<string, number> {
        const totals: Record<string, number> = {};
        const areaFieldKey = this.mapConfig.areaValueFieldKey;
        this.dataInput.forEach(area => {
            const region = canonicalizeRegionName(area?.region ?? area?.name ?? '');
            if (!region) { return; }
            totals[region] = (totals[region] ?? 0) + Number(area?.[areaFieldKey] ?? area?.y ?? 0);
        });

        return totals;
    }

    private buildStateToRegionMap(): Record<string, string> {
        const map: Record<string, string> = {};
        this.dataInput.forEach((areaRow) => {
            const area = canonicalizeRegionName(areaRow?.area ?? areaRow?.region ?? areaRow?.name ?? '');
            const states = areaRow?.state_breakdown ?? areaRow?.stateBreakdown ?? [];
            if (!area || !Array.isArray(states)) {
                return;
            }
            states.forEach((stateRow: any) => {
                const state = `${stateRow?.state ?? stateRow?.stateCode ?? ''}`.trim().toLowerCase();
                if (state) {
                    map[state.startsWith('us-') ? state : `us-${state}`] = area;
                }
            });
        });
        for (const [region, states] of Object.entries(REGION_STATES)) {
            for (const state of states) {
                map[state] ??= region;
            }
        }
        return map;
    }

    private async initMap(): Promise<void> {
        const topology = await fetch(
            'https://code.highcharts.com/mapdata/countries/us/us-all.topo.json'
        ).then(r => r.json());

        const rawData: any[] = (Highcharts as any).geojson(topology);
        const mapView = topology.objects?.default?.['hc-recommended-mapview'];
        const stateToRegion = this.buildStateToRegionMap();
        this.stateToRegionMap = stateToRegion;
        const stateDealerData = this.buildStateDealerData();
        const regionMetric = this.buildRegionMetricMap();

        const seriesByRegion: Record<string, any[]> = {
            'Western Area': [], 'Southern Area': [], 'Central Area': [], 'Eastern Area': [],
        };

        rawData.forEach(d => {
            const hcKey: string = d.properties['hc-key'];
            const region = stateToRegion[hcKey] ?? EASTERN_AREA;
            const sd = stateDealerData[hcKey];
            const areaMetric = regionMetric[region] ?? 0;
            const stateMetric = Number(sd?.[this.mapConfig.stateValueFieldKey as keyof typeof sd] ?? 0);
            seriesByRegion[region] ??= [];
            seriesByRegion[region].push({
                ...d,
                region,
                value: areaMetric,
                // stored in custom so Highcharts preserves them on the Point object
                custom: {
                    hcKey,
                    region,
                    dealerCount: sd?.dealerCount ?? 0,
                    stateMetric,
                    areaMetric,
                    areaMetricLabel: this.mapConfig.areaMetricLabel,
                    stateMetricLabel: this.mapConfig.stateMetricLabel,
                    valueType: this.mapConfig.valueType ?? 'number',
                },
            });
        });
        const mapSeries: any[] = this.buildMapSeries(seriesByRegion, mapView);

        this.chartOptions = {
            chart: { events: {} },
            title: { text: undefined },
            subtitle: { text: undefined },
            colorAxis: undefined as any,
            mapView,
            mapNavigation: {
                enabled: true,
                enableMouseWheelZoom: false,
                enableDoubleClickZoom: false,
                enableTouchZoom: false,
                buttonOptions: { verticalAlign: 'bottom' },
            },
            legend: {
                enabled: true,
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
            },
            tooltip: {
                useHTML: true,
                formatter(this: any): string {
                    const c = this.point?.custom ?? {};
                    const dealers: number = c.dealerCount ?? 0;
                    const stateMetric: number = c.stateMetric ?? 0;
                    const areaMetric: number = c.areaMetric ?? 0;
                    const region: string = c.region ?? this.series?.name ?? '';
                    const formatTooltipValue = (value: number): string => {
                        if (c.valueType !== 'currencyMillions') {
                            return `${value}`;
                        }
                        if (value <= 0) {
                            return '$0';
                        }
                        return `$${(value / 1_000_000).toFixed(1)}M`;
                    };
                    return `<b>${this.point?.name}</b><br/>
                            Area: <b>${region}</b><br/>
                            ${c.areaMetricLabel}: <b>${formatTooltipValue(areaMetric)}</b><br/>
                            Dealers: <b>${dealers}</b><br/>
                            ${c.stateMetricLabel}: <b>${formatTooltipValue(stateMetric)}</b>`;
                },
            },
            plotOptions: {
                map: {
                    states: { hover: { brightness: 0.2 } },
                    borderColor: '#ffffff',
                    borderWidth: 0.5,
                    cursor: 'pointer',
                    point: {
                        events: {
                            click: (event: any) => {
                                const point = event?.point ?? {};
                                const hcKey: string =
                                    point.properties?.['hc-key'] ??
                                    point.options?.properties?.['hc-key'] ??
                                    point.custom?.hcKey ??
                                    point.options?.custom?.hcKey ?? '';
                                const name: string = point.name ?? point.options?.name ?? '';
                                this.ngZone.run(() => {
                                    this.onStateClick(hcKey, name);
                                });
                            },
                        },
                    },
                },
            },
            series: mapSeries,
        };

        this.isLoading = false;
        this.updateFlag = true;
    }

    private buildMapSeries(seriesByRegion: Record<string, any[]>, mapView: any): any[] {
        this.mapSeriesData = seriesByRegion;
        this.mapViewConfig = mapView;
        return Object.entries(seriesByRegion)
            .filter(([region]) => !this.isAreaFiltered || region === this.selectedArea)
            .map(([region, data]) => ({
                type: 'map',
                name: region,
                data,
                color: REGION_COLORS[region] ?? '#35aef4',
                custom: { mapView },
                cursor: 'pointer',
                dataLabels: {
                    enabled: true,
                    format: '{point.properties.postal-code}',
                    style: { fontSize: '8px', fontWeight: 'normal', textOutline: 'none' },
                },
            }));
    }

    private applyAreaHighlight(): void {
        if (this.isLoading || !this.chartOptions.series) {
            return;
        }
        this.chartOptions = { ...this.chartOptions, series: this.buildMapSeries(this.mapSeriesData, this.mapViewConfig) };
        this.updateFlag = true;
        setTimeout(() => this.zoomToVisibleSeries());
    }

    onChartInstance(chart: any): void {
        this.chart = chart;
        if (this.isAreaFiltered) {
            setTimeout(() => this.zoomToVisibleSeries());
        }
    }

    private zoomToVisibleSeries(): void {
        this.chart?.mapView?.fitToBounds(undefined, undefined, true);
    }

    onStateClick(hcKey: string, name: string): void {
        this.selectedState = name;
        this.selectedStateKey = hcKey;
        this.selectedStateRegion = this.stateToRegionMap[hcKey] ?? null;
        const apiStateDealers = this.buildStateDataFromAreaRows()[hcKey]?.dealers ?? [];
        this.selectedStateDealers = apiStateDealers
            .sort((a: any, b: any) => Number(b?.[this.mapConfig.sortFieldKey] ?? 0) - Number(a?.[this.mapConfig.sortFieldKey] ?? 0));
    }

    private fetchMapDataAndInit(): Promise<void> {
        if (!this.apiConfig?.urlPath) {
            return this.initMap();
        }

        this.isLoading = true;
        return this.commonService.getDataFromDataSource(this.apiConfig, this.apiConfig.urlPath).then((response) => {
            if (response.status === API_STATUS.Success) {
                const rows = Array.isArray(response.result) ? response.result : [];
                this.dataInput = this.dataTransform ? this.dataTransform(rows) : rows;
            }
        }).finally(() => this.initMap());
    }

    private fetchMapDataAndInitWithAreaFilter(): Promise<void> {
        if (!this.apiConfig?.urlPath) {
            this.applyAreaHighlight();
            return Promise.resolve();
        }

        this.isLoading = true;
        const apiConfig = structuredClone(this.apiConfig);
        if (this.selectedArea) {
            apiConfig.body = {
                ...(apiConfig.body),
                area: this.selectedArea,
            };
        }
        const urlPath = apiConfig.urlPath;
        if (!urlPath) {
            return Promise.resolve();
        }
        return this.commonService.getDataFromDataSource(apiConfig, urlPath).then((response) => {
            if (response.status === API_STATUS.Success) {
                const rows = Array.isArray(response.result) ? response.result : [];
                this.dataInput = this.dataTransform ? this.dataTransform(rows) : rows;
            }
        }).finally(() => this.initMap());
    }
}
